import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import {
  DraftChapter,
  DraftManual,
  DraftPolicyItem,
  DraftPrefaceItem,
  FilterableItem,
  SidebarItem,
} from 'views/App/Portal/Policy/_types/manuals/draft';
import { useQueryClient } from 'react-query';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { AppContext } from 'views/App/context';
import { arrayMove } from '@dnd-kit/sortable';
import { ServerErrorResponse } from 'data_sources/constants';
import useDraftManual from 'views/App/Portal/Policy/DraftManuals/_data_sources/useDraftManual';
import useManageManual from 'views/App/Portal/Policy/DraftManuals/_data_sources/useManageManual';
import useManagePolicy from 'views/App/Portal/Policy/DraftManuals/_data_sources/useManagePolicy';
import errorHelper from 'helpers/errorHelper';
import { enqueueSnackbar } from 'notistack';
import { ManualFilterOptions } from 'views/App/Portal/Policy/_types/enums';
import { useConfirm } from 'material-ui-confirm';
import { translate as t } from 'hooks/useTranslations';
import contentHelper from 'helpers/contentHelper';
import ContentConversionHelper, { SafeHtmlParser } from '../../helpers/contentConversionHelper';

const DIALOG_TIMEOUT = 120000;
const FINAL_TIMEOUT = 60000;

interface ManualStructureContextProps {
  reorderedManual: DraftManual | null;
  filter: ManualFilterOptions;
  selectedChapter: string | null;
  isSaving: boolean;
  handleFilterChange: (event: any) => void;
  handleChapterClick: (chapterId: string) => void;
  handleDragEndPrefaces: (event: any) => void;
  handleDragEndChapters: (event: any) => void;
  handleDragEndPolicies: (event: any) => void;
  unlockManual: () => void;
  handleSave: () => Promise<void>;
  handleToggleState: (type: 'preface' | 'chapter' | 'policy', id: string, event: React.MouseEvent) => void;
  handleDelete: (type: 'chapter' | 'policy', id: string, event: React.MouseEvent) => void;
  wasAlreadyDisabled: (type: 'preface' | 'chapter' | 'policy', itemId: string) => boolean;
  getFilteredItems: (type: 'prefaces' | 'chapters' | 'policies') => any;
  getContent: (content: string) => string | null;
  sanitizeContent: (htmlContent?: string) => JSX.Element;
  showDialog: boolean;
  handleDialogConfirm: () => void;
  sidebarItem: SidebarItem | null;
  setSidebarItem: (item: SidebarItem | null) => void;
  hoveredItem: DraftPrefaceItem | DraftPolicyItem | null;
  setHoveredItem: (item: DraftPrefaceItem | DraftPolicyItem | null) => void;
  anchorEl: HTMLElement | null;
  setAnchorEl: (element: HTMLElement | null) => void;
  popoverPlacement: 'top' | 'bottom';
  setPopoverPlacement: (placement: 'top' | 'bottom') => void;
}

const ManualStructureContext = createContext<ManualStructureContextProps | undefined>(undefined);

export const ManualStructureProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { update: updateManual, removeLock: removeLockManual } = useManageManual();
  const { updateDraftStatus, approveChanges, getLock, removeLock } = useManagePolicy();
  const { currentProfile } = useContext(AppContext);
  const [reorderedManual, setReorderedManual] = useState<DraftManual | null>(null);
  const [originalManual, setOriginalManual] = useState<DraftManual | null>(null);
  const [filter, setFilter] = useState<ManualFilterOptions>(ManualFilterOptions.ALL);
  const [sidebarItem, setSidebarItem] = useState<SidebarItem | null>(null);
  const [hoveredItem, setHoveredItem] = useState<DraftPrefaceItem | DraftPolicyItem | null>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [popoverPlacement, setPopoverPlacement] = useState<'top' | 'bottom'>('bottom');
  const [selectedChapter, setSelectedChapter] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [showDialog, setShowDialog] = useState<boolean>(false);
  const mainTimerRef = useRef<NodeJS.Timeout | null>(null);
  const secondaryTimerRef = useRef<NodeJS.Timeout | null>(null);
  const confirm = useConfirm();

  const { id } = useParams();
  const manualQuery = useDraftManual(id, {
    onSuccess: (data: DraftManual) => {
      setReorderedManual(data ?? ({} as DraftManual));
    },
  });

  useEffect(() => {
    if (manualQuery.data && manualQuery.data?.lock?.userId !== currentProfile?.user?.id) {
      navigate(-1);
    }
    const data = manualQuery.data ?? ({} as DraftManual);
    setReorderedManual(data);
    setOriginalManual(data);
  }, [manualQuery.data, currentProfile?.user?.id, navigate]);

  const clearTimers = () => {
    if (mainTimerRef.current) {
      clearTimeout(mainTimerRef.current);
    }
    if (secondaryTimerRef.current) {
      clearTimeout(secondaryTimerRef.current);
    }
  };

  const resetTimer = useCallback(() => {
    clearTimers();
    mainTimerRef.current = setTimeout(() => {
      setShowDialog(true);
      secondaryTimerRef.current = setTimeout(() => {
        handleInactivityTimeout();
      }, FINAL_TIMEOUT);
    }, DIALOG_TIMEOUT);
  }, []);

  const wasAlreadyDisabled = useCallback(
    (type: 'preface' | 'chapter' | 'policy', itemId: string): boolean => {
      if (!originalManual) return false;
      if (type === 'preface') {
        return originalManual.prefaces?.find((p) => p.id === itemId)?.state === 'DISABLED';
      }
      if (type === 'chapter') {
        return originalManual.chapters?.find((c) => c.id === itemId)?.state === 'DISABLED';
      }
      if (type === 'policy') {
        for (const chapter of originalManual.chapters ?? []) {
          const policy = chapter.policies?.find((p) => p.id === itemId);
          if (policy) return policy.state === 'DISABLED';
        }
        return false;
      }
      return false;
    },
    [originalManual]
  );

  // Function to identify policies that have change the status from enabled to disabled and from disabled to enabled
  const getChangedStatusPolicies = (): { policyId: string; chapterId: string }[] => {
    if (!originalManual || !reorderedManual) return [];

    const changeStatusPolicies: { policyId: string; chapterId: string }[] = [];

    reorderedManual.chapters.forEach((chapter) => {
      const originalChapter = originalManual.chapters.find((c) => c.id === chapter.id);
      if (!originalChapter) return;

      chapter.policies?.forEach((policy) => {
        const originalPolicy = originalChapter.policies?.find((p) => p.id === policy.id);
        if (
          originalPolicy &&
          ((originalPolicy.state === 'ENABLED' && policy.state === 'DISABLED') ||
            (originalPolicy.state === 'DISABLED' && policy.state === 'ENABLED'))
        ) {
          changeStatusPolicies.push({ policyId: policy.id, chapterId: chapter.id });
        }
      });
    });

    return changeStatusPolicies;
  };

  // Function to update policy status to IN_APPROVAL and then APPROVED
  const approvePolicyStatus = async (policyId: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      // Step 1: Get lock on the policy
      getLock(
        reorderedManual!.id,
        policyId,
        () => {
          // Step 2: Remove lock on the policy
          removeLock(
            reorderedManual!.id,
            policyId,
            () => {
              // Step 3: Update draftState to IN_APPROVAL
              updateDraftStatus(
                reorderedManual!.id,
                policyId,
                'IN_APPROVAL',
                () => {
                  // Step 4: Approve changes
                  approveChanges(
                    reorderedManual!.id,
                    policyId,
                    () => {
                      resolve();
                    },
                    (error: ServerErrorResponse) => {
                      errorHelper.enqueueErrors(error);
                      reject(error);
                    },
                  );
                },
                (error: ServerErrorResponse) => {
                  errorHelper.enqueueErrors(error);
                  reject(error);
                },
              );
            },
            (error: ServerErrorResponse) => {
              errorHelper.enqueueErrors(error);
              reject(error);
            },
          );
        },
        (error: ServerErrorResponse) => {
          errorHelper.enqueueErrors(error);
          reject(error);
        },
      );
    });
  };

  const unlockManual = async () => {
    if (reorderedManual?.id) {
      removeLockManual(
        reorderedManual.id,
        () => {
          queryClient.invalidateQueries(['draft_manual', reorderedManual.id]);
          queryClient.invalidateQueries(['draft_policy', reorderedManual.id]);
        },
        (error: ServerErrorResponse) => {
          errorHelper.enqueueErrors(error);
        },
      );
    }
  };

  const handleInactivityTimeout = () => {
    if (reorderedManual?.id) {
      removeLockManual(
        reorderedManual.id,
        () => {
          queryClient.invalidateQueries(['draft_manual', reorderedManual.id]);
          queryClient.invalidateQueries(['draft_policy', reorderedManual.id]);
          enqueueSnackbar(
            'No actions were performed for an extended period of time. You have been moved back to your previous page.',
            {
              variant: 'error',
              autoHideDuration: 6000,
            },
          );
          navigate(-1);
        },
        (error: ServerErrorResponse) => {
          errorHelper.enqueueErrors(error);
        },
      );
    }
  };

  useEffect(() => {
    resetTimer();
    return () => {
      clearTimers();
    };
  }, [reorderedManual, resetTimer]);

  const handleFilterChange = (event: any) => {
    setFilter(event.target.value as ManualFilterOptions);
    resetTimer();
  };

  const handleChapterClick = (chapterId: string) => {
    if (selectedChapter !== chapterId) {
      setSelectedChapter(chapterId);
    } else {
      setSelectedChapter(null);
    }
    resetTimer();
  };

  const handleDragEndPrefaces = (event: any) => {
    const { active, over } = event;
    if (active.id !== over.id) {
      setReorderedManual((prevManual) => {
        if (!prevManual) return prevManual;
        const oldIndex = prevManual.prefaces.findIndex((p) => p.id === active.id);
        const newIndex = prevManual.prefaces.findIndex((p) => p.id === over.id);
        const newPrefaces = arrayMove(prevManual.prefaces, oldIndex, newIndex);
        return { ...prevManual, prefaces: newPrefaces };
      });
    }
    resetTimer();
  };

  const handleDragEndChapters = (event: any) => {
    const { active, over } = event;
    if (active.id !== over.id) {
      setReorderedManual((prevManual) => {
        if (!prevManual) return prevManual;
        const oldIndex = prevManual.chapters.findIndex((c) => c.id === active.id);
        const newIndex = prevManual.chapters.findIndex((c) => c.id === over.id);
        const newChapters = arrayMove(prevManual.chapters, oldIndex, newIndex);
        return { ...prevManual, chapters: newChapters };
      });
    }
    resetTimer();
  };

  const handleDragEndPolicies = (event: any) => {
    const { active, over } = event;

    if (!over || active.id === over.id) return;

    setReorderedManual((prevManual) => {
      if (!prevManual) return prevManual;

      const chapterContainingPolicy = prevManual.chapters.find((chapter) =>
        chapter.policies?.some((policy) => policy.id === active.id),
      );

      if (!chapterContainingPolicy) return prevManual;

      const oldIndex = chapterContainingPolicy.policies?.findIndex((policy) => policy.id === active.id);
      const newIndex = chapterContainingPolicy.policies?.findIndex((policy) => policy.id === over.id);

      const updatedManual = JSON.parse(JSON.stringify(prevManual));
      const updatedChapter = updatedManual.chapters.find((chapter: any) => chapter.id === chapterContainingPolicy.id);

      if (updatedChapter) {
        const reorderedPolicies = arrayMove(updatedChapter.policies, oldIndex || 0, newIndex || 0);
        updatedChapter.policies = reorderedPolicies;
      }

      return updatedManual;
    });
    resetTimer();
  };

  const handleSave = async () => {
    if (!reorderedManual) return;
    setIsSaving(true);
    setReorderedManual(reorderedManual);

    try {
      await updateManual(reorderedManual.id, reorderedManual);
    } catch (err) {
      let serverError: ServerErrorResponse = {
        message: 'An unexpected error occurred.',
        errorDetails: null,
      };
      try {
        const parsed = JSON.parse((err as Error).message);
        serverError = { ...serverError, ...parsed, message: parsed.message ?? serverError.message };
      } catch {
        // use default serverError
      }
      errorHelper.enqueueErrors(serverError);
      setIsSaving(false);
      return;
    }

    // Get list of policies that have changed their status from Enabled to disabled and from disabled to enabled
    const changedStatusPolicies = getChangedStatusPolicies();

    // Update status for each disabled/enabled policy
    if (changedStatusPolicies.length > 0) {
      try {
        await Promise.all(changedStatusPolicies.map(({ policyId }) => approvePolicyStatus(policyId)));

        // Invalidate queries to refresh data
        queryClient.invalidateQueries(['draft_manuals']);
        queryClient.invalidateQueries(['approved_content']);
        queryClient.invalidateQueries(['draft_manual', reorderedManual.id]);
        queryClient.invalidateQueries(['draft_policy', reorderedManual.id]);
      } catch (error) {
        console.error('Error updating disabled policy statuses:', error);
        // Continue with the save process even if policy status updates fail
      }
    }

    removeLockManual(
      reorderedManual.id,
      () => {
        queryClient.invalidateQueries(['draft_manual', reorderedManual.id]);
        queryClient.invalidateQueries(['draft_policy', reorderedManual.id]);
        enqueueSnackbar('Structure saved succesfully', {
          variant: 'success',
          autoHideDuration: 4000,
        });
        setIsSaving(false);
        navigate(-1);
      },
      (error: ServerErrorResponse) => {
        errorHelper.enqueueErrors(error);
        setIsSaving(false);
      },
    );
  };

  const handleToggleState = async (type: 'preface' | 'chapter' | 'policy', id: string, event: React.MouseEvent) => {
    event.stopPropagation();

    if (type === 'chapter') {
      const chapter = reorderedManual?.chapters.find((c) => c.id === id);
      if (!chapter) return;

      if (chapter.state === 'ENABLED') {
        const enabledPolicies = chapter.policies?.filter((p) => p.state === 'ENABLED');
        if (enabledPolicies && enabledPolicies.length > 0) {
          confirm({
            description: 'Disabling this chapter will disable all policies within it. Do you want to proceed?',
            confirmationText: t('common.ok'),
            cancellationText: t('common.cancel'),
          })
            .then(() => {
              setReorderedManual((prevManual) => {
                if (!prevManual) return prevManual;
                const updatedManual = { ...prevManual };
                updatedManual.chapters = updatedManual.chapters.map((c) => {
                  if (c.id === id) {
                    return {
                      ...c,
                      state: 'DISABLED',
                      policies: c.policies?.map((p) => ({ ...p, state: 'DISABLED' })) || [],
                    };
                  }
                  return c;
                });
                return updatedManual;
              });
            })
            .catch(() => {
              event.preventDefault();
              return false;
            });
        } else {
          setReorderedManual((prevManual) => {
            if (!prevManual) return prevManual;
            const updatedManual = { ...prevManual };
            updatedManual.chapters = updatedManual.chapters.map((c) => {
              if (c.id === id) {
                return { ...c, state: 'DISABLED' };
              }
              return c;
            });
            return updatedManual;
          });
        }
      } else {
        setReorderedManual((prevManual) => {
          if (!prevManual) return prevManual;
          const updatedManual = { ...prevManual };
          updatedManual.chapters = updatedManual.chapters.map((c) => {
            if (c.id === id) {
              return {
                ...c,
                state: 'ENABLED',
                policies: c.policies?.map((p) => ({ ...p, state: 'ENABLED' })) || [],
              };
            }
            return c;
          });
          return updatedManual;
        });
      }
    } else if (type === 'policy') {
      const policyWithChapter = reorderedManual?.chapters
        .flatMap((c) => c.policies?.map((p) => ({ ...p, chapterId: c.id })) || [])
        .find((p) => p.id === id);
      if (!policyWithChapter) return;

      if (policyWithChapter.state === 'DISABLED') {
        setReorderedManual((prevManual) => {
          if (!prevManual) return prevManual;
          const updatedManual = { ...prevManual };
          updatedManual.chapters = updatedManual.chapters.map((c) => {
            if (c.id === policyWithChapter.chapterId) {
              return {
                ...c,
                state: 'ENABLED', // Enable the chapter if it's disabled
                policies:
                  c.policies?.map((p) => {
                    if (p.id === id) {
                      return { ...p, state: 'ENABLED' };
                    }
                    return p;
                  }) || [],
              };
            }
            return c;
          });
          return updatedManual;
        });
      } else {
        setReorderedManual((prevManual) => {
          if (!prevManual) return prevManual;
          const updatedManual = { ...prevManual };
          updatedManual.chapters = updatedManual.chapters.map((c) => {
            if (c.id === policyWithChapter.chapterId) {
              return {
                ...c,
                policies:
                  c.policies?.map((p) => {
                    if (p.id === id) {
                      return { ...p, state: 'DISABLED' };
                    }
                    return p;
                  }) || [],
              };
            }
            return c;
          });
          return updatedManual;
        });
      }
    } else if (type === 'preface') {
      setReorderedManual((prevManual) => {
        if (!prevManual) return prevManual;

        const updatedManual = JSON.parse(JSON.stringify(prevManual));

        updatedManual.prefaces = updatedManual.prefaces.map((preface: any) =>
          preface.id === id
            ? {
                ...preface,
                state: preface.state === 'ENABLED' ? 'DISABLED' : 'ENABLED',
              }
            : preface,
        );

        return updatedManual;
      });
    }
    resetTimer();
  };

  const handleDelete = async (type: 'chapter' | 'policy', id: string, event: React.MouseEvent) => {
    event.stopPropagation();

    const translationKey = type === 'chapter' ? 'deleteChapter' : 'deletePolicy';

    try {
      await confirm({
        title: t(`draftManuals.${translationKey}.title`),
        description: t(`draftManuals.${translationKey}.message`),
        confirmationText: t(`draftManuals.${translationKey}.confirm`),
        cancellationText: t(`draftManuals.${translationKey}.cancel`),
        confirmationButtonProps: { variant: 'contained', color: 'error' },
        cancellationButtonProps: { variant: 'outlined', color: 'inherit' },
      });

      if (type === 'chapter') {
        setReorderedManual((prevManual) => {
          if (!prevManual) return prevManual;
          const updatedManual = { ...prevManual };
          updatedManual.chapters = updatedManual.chapters.map((c) => {
            if (c.id === id) {
              return {
                ...c,
                isDeleted: true,
                policies: c.policies?.map((p) => ({ ...p, isDeleted: true })) || [],
              };
            }
            return c;
          });
          return updatedManual;
        });
      } else if (type === 'policy') {
        const policyWithChapter = reorderedManual?.chapters
          .flatMap((c) => c.policies?.map((p) => ({ ...p, chapterId: c.id })) || [])
          .find((p) => p.id === id);
        if (!policyWithChapter) return;

        setReorderedManual((prevManual) => {
          if (!prevManual) return prevManual;
          const updatedManual = { ...prevManual };
          updatedManual.chapters = updatedManual.chapters.map((c) => {
            if (c.id === (policyWithChapter as any).chapterId) {
              return {
                ...c,
                policies:
                  c.policies?.map((p) => {
                    if (p.id === id) {
                      return { ...p, isDeleted: true };
                    }
                    return p;
                  }) || [],
              };
            }
            return c;
          });
          return updatedManual;
        });
      }
    } catch (error) {
      // User cancelled the deletion
    }
    resetTimer();
  };

  const applyFilter = (items: FilterableItem[], filter: ManualFilterOptions) => {
    switch (filter) {
      case ManualFilterOptions.DISABLED:
        return items.filter((item) => item.state === ManualFilterOptions.DISABLED);
      case ManualFilterOptions.ENABLED:
        return items.filter((item) => item.state === ManualFilterOptions.ENABLED);
      case ManualFilterOptions.ALL:
      default:
        return items;
    }
  };

  const getFilteredItems = (type: 'prefaces' | 'chapters' | 'policies') => {
    if (!reorderedManual) return [];

    let items: (DraftPrefaceItem | DraftChapter | DraftPolicyItem)[] = [];

    switch (type) {
      case 'prefaces':
        items = reorderedManual.prefaces ?? [];
        break;
      case 'chapters':
        items = reorderedManual.chapters ?? [];
        break;
      case 'policies': {
        if (!selectedChapter) return [];
        const chapter = reorderedManual.chapters.find((c) => c.id === selectedChapter);
        items = chapter ? (chapter.policies ?? []) : [];
        break;
      }
    }

    return applyFilter(items, filter);
  };

  const handleDialogConfirm = () => {
    setShowDialog(false);
    resetTimer();
  };

  const getContent = (contentHTML: string) => {
    const content = contentHelper.decodeHtmlEntities(contentHTML);
    return content;
  };

  const sanitizeContent = (htmlContent?: string): JSX.Element => {
    if (!htmlContent) return <></>;
    const cleanHtml = ContentConversionHelper.contentForRender(htmlContent);
    return <SafeHtmlParser content={cleanHtml} />;
  };

  const contextValue = {
    reorderedManual,
    filter,
    selectedChapter,
    isSaving,
    sidebarItem,
    hoveredItem,
    anchorEl,
    popoverPlacement,
    handleFilterChange,
    handleChapterClick,
    handleDragEndPrefaces,
    handleDragEndChapters,
    handleDragEndPolicies,
    unlockManual,
    handleSave,
    handleToggleState,
    handleDelete,
    wasAlreadyDisabled,
    getFilteredItems,
    getContent,
    sanitizeContent,
    showDialog,
    handleDialogConfirm,
    setSidebarItem,
    setHoveredItem,
    setAnchorEl,
    setPopoverPlacement,
  };

  return <ManualStructureContext.Provider value={contextValue}>{children}</ManualStructureContext.Provider>;
};

export const useManualStructureContext = () => {
  const context = useContext(ManualStructureContext);
  if (!context) {
    throw new Error('useManualStructureContext must be used within a ManualStructureProvider');
  }
  return context;
};
